import cx from "classnames";
import { forwardRef, useEffect, useRef, useState } from "react";
import styles from "./Toaster.module.scss";
import { Alert, AlertProps } from "../Alert";
import { useToasts } from "./ToastProvider";
import { Button, ButtonProps } from "../Button";
import {
  addOrUpdateToastHeight,
  ANIMATION_DURATION,
  useCloseToast,
  useToastPositionStyles,
} from "./toast-utils";
import { useSwipe, useSwipeFn } from "../../hooks";
import { toast } from "./toast-methods";
import { ProgressBar, ProgressBarProps } from "../..";

type ToastAction = Pick<ButtonProps, "appearance" | "onClick"> & {
  label: string;
};

/**
 * Props for the Toast component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ToastProps = {
  // used in toast methods
  /**
   * Action buttons to display in the toast
   */
  actions?: { primary: ToastAction; secondary?: ToastAction };
  /**
   * CSS class name for the toast container
   */
  containerClassName?: string;
  /**
   * Duration in milliseconds before auto-dismiss. Set to false to disable auto-dismiss
   */
  duration?: number | false;
  /**
   * Additional message content to display below the title
   */
  message?: string;
  /**
   * Callback function called when toast is dismissed, expired, or closed
   */
  onDismiss?: () => void;
  /**
   * Progress bar value or indeterminate state
   */
  progress?: ProgressBarProps["value"] | "indeterminate";
  /**
   * CSS class name for the toast element
   */
  toastClassName?: string;

  // used in toast methods and passed to alert
  /**
   * Unique identifier for the toast
   */
  id: string;
  /**
   * This is only run when the "x" button is triggered on the toast.
   * To run a callback when the toast is closed, expired, or dismissed,
   * use the `onDismiss` parameter instead.
   */
  onClose?: AlertProps["onClose"];
  /**
   * Visual status of the toast (info, success, warning, danger)
   */
  status: AlertProps["status"];
  /**
   * Title text displayed in the toast
   */
  title: string;

  // used by toaster (internal)
  /**
   * Timestamp when the toast was created (internal use)
   */
  createdAt?: number;
  /**
   * Position index of the toast in the stack (internal use)
   */
  index?: number;
  /**
   * Whether the toast is below the stack index (internal use)
   */
  isBelowStackIndex?: boolean;
  /**
   * Whether the toast is currently stacked (internal use)
   */
  isStacked?: boolean;
  /**
   * Pause status for hover interactions (internal use)
   */
  pausedStatus?: number | "resumed";
};

/**
 * Toast component for displaying temporary notifications to users.
 *
 * Features:
 * - Supports different visual styles (info, success, warning, danger)
 * - Auto-dismiss with configurable duration
 * - Optional action buttons for user interaction
 * - Progress bar support for loading states
 * - Swipe gestures for dismissal
 * - Stacking behavior for multiple toasts
 * - Pause on hover functionality
 * - Accessible with proper ARIA roles
 * - Smooth animations and transitions
 *
 * @example
 * <Toast
 *   id="toast-1"
 *   status="success"
 *   title="Success!"
 *   message="Your changes have been saved."
 *   duration={5000}
 * />
 */
export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      actions,
      containerClassName,
      createdAt,
      duration: durationProp,
      index = 0,
      isBelowStackIndex,
      isStacked,
      message,
      onClose,
      onDismiss,
      pausedStatus,
      progress,
      toastClassName,
      ...alertProps
    },
    ref,
  ) => {
    const { setToastHeights, extraToastsVisible, isDialogToaster } =
      useToasts();
    const closeToast = useCloseToast(styles.close);
    const toastPositionStyles = useToastPositionStyles(index);

    const toastWrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<NodeJS.Timeout | undefined>();
    const pausedAtRef = useRef<number>(0);
    const createdAtRef = useRef<number>(createdAt ?? 0);
    const hasBeenStackedRef = useRef<boolean>(false);

    const [transitionClass, setTransitionClass] = useState<string | undefined>(
      styles.hidden,
    );
    const [toastOpened, setToastOpened] = useState<boolean>(false);

    // don't allow toasts to auto-dismiss when actions are present
    const duration = actions ? false : durationProp;

    const alertClasses = cx(
      styles["toast-alert"],
      toastClassName,
      transitionClass,
    );

    useEffect(() => {
      // add styles for opening animation then remove
      if (!toastOpened) {
        setTransitionClass(styles.open);

        setTimeout(() => {
          setTransitionClass(undefined);
        }, ANIMATION_DURATION);

        // manually set toast opened since animation is not run
        setToastOpened(true);
      }
      return () => {
        // run when toast is closed
        onDismiss?.();
      };
      // only run this once
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // send height to toast provider for positioning calculations on open
    useEffect(() => {
      if (toastWrapperRef.current && toastOpened && !isStacked) {
        if ((isBelowStackIndex && extraToastsVisible) || isDialogToaster) {
          // animation is disabled when dialog toaster first opens
          const delay = isDialogToaster ? 0 : ANIMATION_DURATION;

          setTimeout(() => {
            const height = toastWrapperRef.current?.clientHeight;
            addOrUpdateToastHeight(alertProps.id, height ?? 0, setToastHeights);
          }, delay); // get correct height after width transition
        } else {
          const height = toastWrapperRef.current.clientHeight;
          addOrUpdateToastHeight(alertProps.id, height, setToastHeights);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      toastOpened,
      isStacked,
      extraToastsVisible,
      isDialogToaster,
      // toast updates
      actions,
      message,
      alertProps.title,
    ]);

    // handle duration timeout and pausing when hovering over toaster
    useEffect(() => {
      if (duration && !isStacked) {
        // initial render
        if (!pausedStatus) {
          timerRef.current = setTimeout(
            () => closeToast(alertProps.id),
            duration,
          );
          return () => clearTimeout(timerRef.current);

          // initial hovering over toaster
        } else if (typeof pausedStatus === "number") {
          clearTimeout(timerRef.current);
          pausedAtRef.current = pausedStatus;

          // after initial hovering over toaster
        } else if (pausedStatus === "resumed") {
          const remainingDuration =
            // rendering new toast after initial hover
            pausedAtRef.current === 0
              ? duration
              : // resume timer after valid hover
                duration + (createdAtRef.current || 0) - pausedAtRef.current;

          timerRef.current = setTimeout(() => {
            closeToast(alertProps.id);
            pausedAtRef.current = 0;
          }, remainingDuration);

          return () => clearTimeout(timerRef.current);
        }
      }
      // don't want to re-render here for alertProps.id, closeToast, or isStacked
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdAtRef.current, duration, pausedStatus]);

    // disable timeout when toasts are stacked
    useEffect(() => {
      if (duration) {
        // stop timer if toast is stacked (hidden)
        if (isStacked) {
          clearTimeout(timerRef.current);
          hasBeenStackedRef.current = true;

          // reset timer after moving out of the stack
        } else if (
          !isStacked &&
          hasBeenStackedRef.current &&
          !(typeof pausedStatus === "number")
        ) {
          createdAtRef.current = Date.now();

          timerRef.current = setTimeout(() => {
            closeToast(alertProps.id);
            hasBeenStackedRef.current = false;
          }, duration);

          return () => clearTimeout(timerRef.current);
        }
      }
      // only run if isStacked or duration change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStacked, duration]);

    const onSwipe = (dir: useSwipeFn["direction"]) => {
      if (dir === "right") {
        toast.dismiss(alertProps.id);
        return;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- prevent direction from being passed to the component
    const { direction, ...touchProps } = useSwipe(onSwipe);

    const alertCloseProps:
      | {
          onClose: AlertProps["onClose"];
        }
      | Record<string, never> =
      isStacked && !extraToastsVisible
        ? {}
        : {
            onClose,
          };

    const hasMessage = message && message !== "";
    const hasProgress = progress != null;

    return (
      <div
        data-anv="toast"
        className={containerClassName}
        style={toastPositionStyles ?? { top: "1.5rem" }}
        ref={toastWrapperRef}
        onAnimationStart={() => setToastOpened(true)}
        role={isStacked ? "presentation" : undefined}
        {...touchProps}
      >
        <Alert
          className={alertClasses}
          {...alertProps}
          {...alertCloseProps}
          ref={ref}
          tabIndex={isStacked && !extraToastsVisible ? -1 : 0}
        >
          {hasMessage || hasProgress || actions ? (
            <>
              {hasMessage ? (
                <div
                  className={styles["message-container"]}
                  id={`toast-message-${alertProps.id}`}
                >
                  {message}
                </div>
              ) : null}

              {hasProgress ? (
                <>
                  {progress === "indeterminate" ? (
                    <ProgressBar
                      aria-labelledby={`toast-message-${alertProps.id}`}
                      indeterminate
                    />
                  ) : (
                    <ProgressBar
                      aria-labelledby={`toast-message-${alertProps.id}`}
                      value={progress}
                    />
                  )}
                </>
              ) : null}

              {actions ? (
                <div className={styles["alert-action"]}>
                  <Button
                    appearance={actions.primary.appearance}
                    onClick={actions.primary.onClick}
                    size="small"
                  >
                    {actions.primary.label}
                  </Button>

                  {actions.secondary && (
                    <Button
                      appearance={actions.secondary.appearance}
                      onClick={actions.secondary.onClick}
                      size="small"
                    >
                      {actions.secondary.label}
                    </Button>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </Alert>
      </div>
    );
  },
);

Toast.displayName = "Toast";
